import { Button, Input, InputNumber, message, Modal, ModalProps, Table, Tooltip } from 'antd';
import StatusButton from 'components/StatusButton';
import { Formik } from 'formik';
import { toPng } from 'html-to-image';
import { useMemo, useRef, useState } from 'react';
import { resourceURLToFile } from 'utils/getImage';
import { useSendFileImage } from '../Chat/components/MessageEditor/Toolbar/ImageUpload';
import { useTranslate } from '@tolgee/react';
import { InvoiceInfo } from 'services/api/conversation';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import dayjs from 'dayjs';
import styled from '@emotion/styled';
import StickyModal from 'components/StickyModal';
import Icon, { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { ReactComponent as AutoTranslateSvg } from '../assets/autoTranslate.svg';
import { modeTranslate } from 'services/api/overseaTranslate';
import Barcode from 'react-barcode';
import { formatNumber } from 'utils/numberFormat';

function InvoiceModal({
  platform,
  region,
  invoiceInfo,
  buyerNick,
  ...otherProps
}: ModalProps & {
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  invoiceInfo: InvoiceInfo;
  buyerNick: string;
}) {
  const { t } = useTranslate();

  const [editing, setEditing] = useState(false);

  const unit = useMemo(() => {
    const {
      items: [{ paidPriceUnit } = {}],
      subtotal,
    } = invoiceInfo;
    if (paidPriceUnit) {
      return paidPriceUnit;
    }
    const [, unit] = subtotal.split(' ');
    return unit;
  }, [invoiceInfo]);

  const serverDateFormat = 'MM/DD/YYYY';
  const dateFormat = 'YYYY-MM-DD';

  const [initialValues, setInitialValues] = useState(() => {
    const transformedInvoiceInfo = {
      ...invoiceInfo,
      invoiceTo: invoiceInfo.invoiceTo || buyerNick,
      orderDate: dayjs(invoiceInfo.orderDate, serverDateFormat).format(dateFormat),
      invoiceDate: dayjs(invoiceInfo.invoiceDate, serverDateFormat).format(dateFormat),
      items: invoiceInfo.items.map(({ productName, paidPrice, qty }) => {
        const item = {
          productName,
          qty: Number(qty),
          paidPrice: Number(paidPrice),
        };
        return item as Partial<typeof item>;
      }),
      subtotal: Number(invoiceInfo.subtotal.split(' ')[0]),
      discount: -Number(invoiceInfo.discount.split(' ')[0]),
      shipping: Number(invoiceInfo.shipping.split(' ')[0]),
      others: Number(invoiceInfo.others.split(' ')[0]),
      netPaid: Number(invoiceInfo.netPaid.split(' ')[0]),
    };
    return {
      title: 'INVOICE',
      ...transformedInvoiceInfo,
      extras: [] as { key: string; value: string }[],
      extraItems: [] as (typeof transformedInvoiceInfo)['items'],
    };
  });

  const contentRef = useRef<HTMLDivElement>(null);

  const sendFileImage = useSendFileImage();

  const systemLang = useSystemLanguageValue();
  const regionLang = regionLangMap[region] || 'en-US';
  const lang = editing ? systemLang : regionLang;
  const getText = (key: keyof typeof texts) =>
    lang === 'es' ? 'en-US' : lang === 'zh-CN' ? key : texts[key][lang];

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values) => {
        setInitialValues(values);
        setEditing(false);
      }}
    >
      {({ dirty, values, setValues, handleSubmit, resetForm }) => {
        const {
          title,
          invoiceNo,
          orderNo,
          orderDate,
          invoiceTo,
          invoiceDate,
          shippingAddress,
          phone,
          extras,
          shopName,
          items,
          extraItems,
          discount,
          shipping,
          others,
        } = values;
        const updateValues = (partial: Partial<typeof values>) =>
          setValues({
            ...values,
            ...partial,
          });
        const textRender = (key: keyof Util.PickByType<typeof values, string>) => {
          return editing ? (
            <Input
              placeholder={t('scene.chatWorkspace.orderTab.invoice.placeholder.enterContent')}
              value={values[key]}
              onChange={({ target: { value } }) => updateValues({ [key]: value })}
            />
          ) : (
            values[key]
          );
        };
        const itemsDataSource = [...items, ...extraItems];
        const updateItem = (index: number, item?: (typeof items)[0]) =>
          updateValues(
            index >= items.length
              ? {
                  extraItems: [
                    ...extraItems.slice(0, index - items.length),
                    ...(item ? [item] : []),
                    ...extraItems.slice(index - items.length + 1),
                  ],
                }
              : {
                  items: [
                    ...items.slice(0, index),
                    ...(item ? [item] : []),
                    ...items.slice(index + 1),
                  ],
                },
          );
        const subtotal = itemsDataSource.reduce((res, { paidPrice }) => res + (paidPrice || 0), 0);
        const amountRender = (amount: number) => (
          <span>
            {formatNumber(amount, '0.00')} {unit}
          </span>
        );
        const editableAmountRender = (key: keyof Util.PickByType<typeof values, number>) =>
          editing ? (
            <span>
              <InputNumber
                placeholder="0"
                min={key === 'discount' || key === 'shipping' ? undefined : 0}
                precision={2}
                addonBefore={key === 'discount' ? '-' : undefined}
                addonAfter={unit}
                value={values[key]}
                onChange={(value) => updateValues({ [key]: value ?? undefined })}
              />
            </span>
          ) : (
            amountRender(key === 'discount' ? -values[key] : values[key])
          );
        return (
          <StickyModal
            width={780}
            centered
            title={
              editing
                ? t('scene.chatWorkspace.orderTab.invoice.action.edit')
                : t('scene.chatWorkspace.orderTab.invoice.title.sendInvoice')
            }
            bodyStyle={{ padding: 0, height: 'calc(100vh - 180px)', overflow: 'auto' }}
            footer={
              editing ? (
                <div>
                  <Button
                    onClick={() => {
                      if (!dirty) {
                        setEditing(false);
                      } else {
                        Modal.confirm({
                          title: t('scene.chatWorkspace.orderTab.invoice.confirmDiscard.title'),
                          content: t(
                            'scene.chatWorkspace.orderTab.invoice.confirmDiscard.description',
                          ),
                          onOk: () => {
                            resetForm();
                            setEditing(false);
                          },
                        });
                      }
                    }}
                  >
                    {t('common.action.cancel')}
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => {
                      if (
                        extras.some(
                          ({ key, value }) => !key || !key.trim() || !value || !value.trim(),
                        )
                      ) {
                        message.error(
                          t('scene.chatWorkspace.orderTab.invoice.placeholder.enterContent'),
                        );
                        return;
                      }
                      if (!shopName || !shopName.trim()) {
                        message.error(
                          t('common.validation.requiredInput', { fieldName: getText('店铺名称') }),
                        );
                        return;
                      }
                      if (
                        itemsDataSource.some(
                          ({ productName }) => !productName || !productName.trim(),
                        )
                      ) {
                        message.error(
                          t('common.validation.requiredInput', { fieldName: getText('商品名称') }),
                        );
                        return;
                      }
                      if (itemsDataSource.some(({ qty }) => !qty && qty !== 0)) {
                        message.error(
                          t('common.validation.requiredInput', { fieldName: getText('数量') }),
                        );
                        return;
                      }
                      if (itemsDataSource.some(({ paidPrice }) => !paidPrice && paidPrice !== 0)) {
                        message.error(
                          t('common.validation.requiredInput', { fieldName: getText('价格') }),
                        );
                        return;
                      }
                      if (!discount && discount !== 0 && platform !== 'MERCADO') {
                        message.error(
                          t('common.validation.requiredInput', { fieldName: getText('优惠金额') }),
                        );
                        return;
                      }
                      if (!shipping && shipping !== 0 && platform !== 'MERCADO') {
                        message.error(
                          t('common.validation.requiredInput', { fieldName: getText('运费') }),
                        );
                        return;
                      }
                      if (!others && others !== 0 && platform !== 'MERCADO') {
                        message.error(
                          t('common.validation.requiredInput', { fieldName: getText('税费&其他') }),
                        );
                        return;
                      }
                      handleSubmit();
                    }}
                  >
                    {t('common.action.save')}
                  </Button>
                </div>
              ) : (
                <div>
                  <Button onClick={(e) => otherProps.onCancel?.(e)}>
                    {t('common.action.cancel')}
                  </Button>
                  <Button onClick={() => setEditing(true)}>{t('common.action.edit')}</Button>
                  <StatusButton
                    type="primary"
                    onClick={() => {
                      return (
                        contentRef.current
                          ? toPng(contentRef.current, { pixelRatio: 1 })
                          : Promise.reject(new Error('No dom'))
                      )
                        .then(resourceURLToFile)
                        .then(sendFileImage)
                        .then(() => otherProps.onCancel?.({} as any))
                        .catch(() => {
                          message.error(t('scene.chatWorkspace.orderTab.error.operationFailed'));
                        });
                    }}
                  >
                    {t('scene.chatWorkspace.orderTab.invoice.title.sendInvoice')}
                  </StatusButton>
                </div>
              )
            }
            {...otherProps}
          >
            <Container ref={contentRef}>
              <h2>
                {editing ? (
                  <Input
                    value={title}
                    onChange={({ target: { value } }) => updateValues({ title: value })}
                  />
                ) : (
                  title
                )}
              </h2>
              <Basic lang={lang}>
                <ul>
                  <li>
                    {(editing || invoiceNo) && <Key>{getText('Invoice No')}:</Key>}
                    <Value>{textRender('invoiceNo')}</Value>
                  </li>
                  <li>
                    {(editing || orderNo) && <Key>{getText('Order No')}:</Key>}
                    <Value>{textRender('orderNo')}</Value>
                  </li>
                  <li>
                    {(editing || orderDate) && <Key>{getText('订单日期')}:</Key>}
                    <Value>{textRender('orderDate')}</Value>
                  </li>
                  <li>
                    {(editing || invoiceTo) && <Key>{getText('发票接收人')}:</Key>}
                    <Value>{textRender('invoiceTo')}</Value>
                  </li>
                  <li>
                    {(editing || invoiceDate) && <Key>{getText('发票日期')}:</Key>}
                    <Value>{textRender('invoiceDate')}</Value>
                  </li>
                </ul>
                <section>
                  <div>
                    {(editing || shippingAddress) && <Key>{getText('送货地址')}:</Key>}
                    <div className="whitespace-pre-wrap">
                      {editing ? (
                        <Input.TextArea
                          placeholder={t(
                            'scene.chatWorkspace.orderTab.invoice.placeholder.enterContent',
                          )}
                          rows={6}
                          value={shippingAddress}
                          onChange={({ target: { value } }) =>
                            updateValues({ shippingAddress: value })
                          }
                        />
                      ) : (
                        shippingAddress
                      )}
                    </div>
                  </div>
                  <span>
                    {(editing || phone) && <Key>{getText('电话')}:</Key>}
                    <Value>{textRender('phone')}</Value>
                  </span>
                </section>
              </Basic>
              {extras.length > 0 && (
                <Extra>
                  {extras.map(({ key, value }, index) => {
                    const update = (extra?: (typeof extras)[0]) =>
                      updateValues({
                        extras: [
                          ...extras.slice(0, index),
                          ...(extra ? [extra] : []),
                          ...extras.slice(index + 1),
                        ],
                      });
                    return editing ? (
                      <section>
                        <Input
                          placeholder={t(
                            'scene.chatWorkspace.orderTab.invoice.placeholder.enterContent',
                          )}
                          value={key}
                          onChange={(e) => update({ key: e.target.value, value })}
                          suffix={
                            <Tooltip
                              title={t('scene.chatWorkspace.orderTab.invoice.action.translate')}
                            >
                              <StatusButton
                                size="small"
                                type="text"
                                icon={<Icon component={AutoTranslateSvg} />}
                                onClick={() =>
                                  modeTranslate(key, regionLang, 'GOOGLE')
                                    .then((res) => {
                                      update({ key: res, value });
                                    })
                                    .catch(() => {
                                      message.error(
                                        t('scene.chatWorkspace.orderTab.error.translationFailed'),
                                      );
                                    })
                                }
                              />
                            </Tooltip>
                          }
                        />
                        :
                        <Input
                          placeholder={t(
                            'scene.chatWorkspace.orderTab.invoice.placeholder.enterContent',
                          )}
                          value={value}
                          onChange={(e) => update({ value: e.target.value, key })}
                          suffix={
                            <Tooltip
                              title={t('scene.chatWorkspace.orderTab.invoice.action.translate')}
                            >
                              <StatusButton
                                size="small"
                                type="text"
                                icon={<Icon component={AutoTranslateSvg} />}
                                onClick={() =>
                                  modeTranslate(value, regionLang, 'GOOGLE')
                                    .then((res) => {
                                      update({ value: res, key });
                                    })
                                    .catch(() => {
                                      message.error(
                                        t('scene.chatWorkspace.orderTab.error.translationFailed'),
                                      );
                                    })
                                }
                              />
                            </Tooltip>
                          }
                        />
                        <Tooltip title={t('common.action.delete')}>
                          <Button type="text" icon={<DeleteOutlined />} onClick={() => update()} />
                        </Tooltip>
                      </section>
                    ) : (
                      <div>
                        <Key>{key}:</Key>
                        <Value>{value}</Value>
                      </div>
                    );
                  })}
                </Extra>
              )}
              {editing && (
                <Button
                  ghost
                  block
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => updateValues({ extras: [...extras, { key: '', value: '' }] })}
                >
                  {t('common.action.add')}
                </Button>
              )}
              {!editing && orderNo && (
                <BarCodeContent>
                  <Barcode displayValue={false} value={orderNo} height={40} width={1} margin={0} />
                  <div>
                    <Key>{getText('Order No')}:</Key>
                    <Value>{orderNo}</Value>
                  </div>
                </BarCodeContent>
              )}
              <StoreInfo>
                <Key>{getText('店铺名称')}:</Key>
                <Value>{textRender('shopName')}</Value>
              </StoreInfo>
              <Detail>
                <Key>{getText('订单详情')}</Key>
                <Table
                  bordered
                  pagination={false}
                  dataSource={itemsDataSource}
                  columns={[
                    {
                      key: 'index',
                      render: (_, __, index) => index + 1,
                    },
                    {
                      key: 'productName',
                      dataIndex: 'productName',
                      title: getText('商品名称'),
                      render: (productName, record, index) => {
                        if (!editing) {
                          return productName;
                        }
                        const update = (productName: string) =>
                          updateItem(index, { ...record, productName });
                        return (
                          <div className="productName">
                            <Input.TextArea
                              rows={2}
                              placeholder={t('common.validation.requiredInput', {
                                fieldName: getText('商品名称'),
                              })}
                              value={productName}
                              onChange={(e) => update(e.target.value)}
                            />
                            <Tooltip
                              title={t('scene.chatWorkspace.orderTab.invoice.action.translate')}
                            >
                              <StatusButton
                                type="text"
                                icon={<Icon component={AutoTranslateSvg} />}
                                onClick={() =>
                                  modeTranslate(productName, regionLang, 'GOOGLE')
                                    .then(update)
                                    .catch(() => {
                                      message.error(
                                        t('scene.chatWorkspace.orderTab.error.translationFailed'),
                                      );
                                    })
                                }
                              />
                            </Tooltip>
                          </div>
                        );
                      },
                    },
                    {
                      key: 'qty',
                      dataIndex: 'qty',
                      title: getText('数量'),
                      render: (qty, record, index) => {
                        if (!editing) {
                          return qty;
                        }
                        return (
                          <InputNumber
                            placeholder="0"
                            min={1}
                            precision={0}
                            value={qty}
                            onChange={(qty) =>
                              updateItem(index, { ...record, qty: qty ?? undefined })
                            }
                          />
                        );
                      },
                    },
                    {
                      key: 'paidPrice',
                      dataIndex: 'paidPrice',
                      title: getText('价格'),
                      render: (paidPrice, record, index) => {
                        if (!editing) {
                          return (
                            <div className="text-right">
                              {formatNumber(paidPrice, '0.00')}
                              <br />
                              {unit}
                            </div>
                          );
                        }
                        const input = (
                          <InputNumber
                            placeholder={getText('价格')}
                            addonAfter={unit}
                            min={0}
                            precision={2}
                            value={paidPrice}
                            onChange={(paidPrice) =>
                              updateItem(index, { ...record, paidPrice: paidPrice ?? undefined })
                            }
                          />
                        );
                        const isExtra = index >= items.length;
                        if (!isExtra) {
                          return <div className="price">{input}</div>;
                        }
                        return (
                          <div className="price">
                            {input}
                            <Button
                              type="text"
                              icon={<DeleteOutlined />}
                              onClick={() => updateItem(index)}
                            />
                          </div>
                        );
                      },
                    },
                  ]}
                />
              </Detail>
              {editing && (
                <Button
                  ghost
                  block
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => updateItem(itemsDataSource.length, {})}
                >
                  {t('scene.chatWorkspace.orderTab.invoice.action.addProduct')}
                </Button>
              )}
              <Amount>
                <div>
                  <Key>{getText('总计金额')}:</Key>
                  {platform !== 'MERCADO' && [
                    <Key>{getText('优惠金额')}:</Key>,
                    <Key>{getText('运费')}:</Key>,
                    <Key>{getText('税费&其他')}:</Key>,
                    <div />,
                    <Key>{getText('需支付费用')}:</Key>,
                  ]}
                </div>
                <div>
                  {amountRender(subtotal)}
                  {platform !== 'MERCADO' && [
                    editableAmountRender('discount'),
                    editableAmountRender('shipping'),
                    editableAmountRender('others'),
                    <div />,
                    amountRender(subtotal - (discount || 0) + (shipping || 0) + (others || 0)),
                  ]}
                </div>
              </Amount>
            </Container>
          </StickyModal>
        );
      }}
    </Formik>
  );
}

const regionLangMap: Partial<Record<General.UpperCaseRegion, General.SystemLanguage>> = {
  ID: 'id',
  TH: 'th',
  VN: 'vi',
  PH: 'tl',
  BR: 'pt',
};

const texts = {
  'Invoice No': {
    'en-US': 'Invoice No',
    id: 'Nomor Invoice',
    th: 'เลขที่ใบแจ้งหนี้',
    tl: 'Invoice No',
    vi: 'Số hóa đơn',
    pt: 'Número NF',
  },
  'Order No': {
    'en-US': 'Order No',
    id: 'Nomor Pesanan',
    th: 'เลขที่คำสั่งซื้อ',
    tl: 'Order No',
    vi: 'Mã đơn hàng',
    pt: 'Número de pedido',
  },
  订单日期: {
    'en-US': 'Order Date',
    id: 'Tanggal Pesanan',
    th: 'วันที่สั่งซื้อ',
    tl: 'Order Date',
    vi: 'Ngày giờ đơn hàng',
    pt: 'Data de pedido',
  },
  发票接收人: {
    'en-US': 'Invoice To',
    id: 'Invoice Perusahaan',
    th: 'ชื่อลูกค้า',
    tl: 'Invoice To',
    vi: 'Hóa đơn đến',
    pt: 'Faturado para',
  },
  发票日期: {
    'en-US': 'Invoice Date',
    id: 'Tanggal Invoice',
    th: 'วันที่ออกใบแจ้งหนี้',
    tl: 'Invoice Date',
    vi: 'Ngày giờ hóa đơn',
    pt: 'Data de emissão',
  },
  送货地址: {
    'en-US': 'Shipping Address',
    id: 'Alamat pengiriman',
    th: 'ที่อยู่รับสินค้า',
    tl: 'Shipping Address',
    vi: 'Địa chỉ nhận hàng',
    pt: 'Endereço de entrega',
  },
  电话: {
    'en-US': 'Phone',
    id: 'Telepon',
    th: 'เบอร์โทรศัพท์',
    tl: 'Phone',
    vi: 'Điện thoại',
    pt: 'Telefone',
  },
  店铺名称: {
    'en-US': 'Shop Name',
    id: 'Nama Toko',
    th: 'ชื่อร้านค้า',
    tl: 'Shop Name',
    vi: 'Tên cửa hàng',
    pt: 'Nome de loja',
  },
  订单详情: {
    'en-US': 'Order Detail',
    id: 'Detail Pesanan',
    th: 'รายละเอียดคำสั่งซื้อ',
    tl: 'Order Detail',
    vi: 'Chi tiết đơn hàng',
    pt: 'Detalhes de pedido',
  },
  商品名称: {
    'en-US': 'Product Name',
    id: 'Nama Produk',
    th: 'ชื่อสินค้า',
    tl: 'Product Name',
    vi: 'Tên sản phẩm',
    pt: 'Nome de produto',
  },
  数量: {
    'en-US': 'Qty',
    id: 'Jumlah',
    th: 'จำนวน',
    tl: 'Qty',
    vi: 'SL',
    pt: 'Qtd.',
  },
  价格: {
    'en-US': 'Paid Price',
    id: 'Jumlah yang Dibayar',
    th: 'ราคารวม',
    tl: 'Paid Price',
    vi: 'Giá cả',
    pt: 'Preço pago',
  },
  总计金额: {
    'en-US': 'Subtotal',
    id: 'Subtotal',
    th: 'จํานวนเงิน',
    tl: 'Subtotal',
    vi: 'Tổng phụ',
    pt: 'Subtotal',
  },
  优惠金额: {
    'en-US': 'Discount',
    id: 'Diskon',
    th: 'ส่วนลด',
    tl: 'Discount',
    vi: 'Chiết khấu',
    pt: 'Desconto',
  },
  运费: {
    'en-US': 'Shipping',
    id: 'Pengiriman',
    th: 'ค่าขนส่ง',
    tl: 'Shipping',
    vi: 'Phí vận chuyển',
    pt: 'Frete',
  },
  '税费&其他': {
    'en-US': 'Taxes & others',
    id: 'Pajak dan lainnya',
    th: 'ภาษีและอื่นๆ',
    tl: 'Taxes & atbp.',
    vi: 'Thuế và mục khác',
    pt: 'Taxas & outros',
  },
  需支付费用: {
    'en-US': 'Net Paid',
    id: 'Harga Nett',
    th: 'จํานวนเงินสุทธิ',
    tl: 'Net Paid',
    vi: 'Thực trả',
    pt: 'Pagamento líquido',
  },
} as const;

const Container = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fff;
  > h2 > .ant-input {
    width: 380px;
  }
  > button {
    margin-top: 12px;
    border-style: dashed;
  }
`;

const Key = styled.span`
  flex: 0 0 auto;
  margin-right: 4px;
  font-weight: 500;
  color: #232426;
`;

const Value = styled.div`
  flex: 1 0 0;
  white-space: pre-wrap;
  word-break: break-all;
`;

const Basic = styled.div<{ lang: General.SystemLanguage }>`
  width: 100%;
  display: flex;
  align-items: stretch;
  border: solid 1px #d3d7e0;
  > * {
    flex: 1 0 0;
    overflow: hidden;
  }
  > ul {
    margin: 0;
    padding: 0;
    list-style: none;
    border-right: solid 1px #d3d7e0;
    > li {
      display: flex;
      align-items: center;
      min-height: 48px;
      padding: 0 12px;
      > span {
        width: ${({ lang }) =>
          `${
            {
              'zh-CN': 80,
              'en-US': 90,
              id: 135,
              th: 115,
              tl: 90,
              vi: 130,
              pt: 130,
              es: 90,
            }[lang]
          }px`};
      }
      > div > * {
        width: 100%;
      }
    }
    > li + li {
      border-top: solid 1px #d3d7e0;
    }
  }
  > section {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    > div {
      flex: 1 0 0;
      padding: 12px;
      textarea {
        resize: none;
      }
    }
    > span {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      min-height: 48px;
      padding: 0 12px;
      border-top: solid 1px #d3d7e0;
    }
  }
`;

const Extra = styled.div`
  border: solid 1px #d3d7e0;
  border-top: none;
  align-self: stretch;
  > * {
    display: flex;
    align-items: center;
    min-height: 48px;
    padding: 0 12px;
  }
  > * + * {
    border-top: solid 1px #d3d7e0;
  }
  > section {
    > span:first-of-type {
      width: 192px;
      margin-right: 4px;
    }
    > span:last-of-type {
      flex: 1 0 0;
      margin: 0 4px;
    }
  }
`;

const BarCodeContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 16px;
  > div {
    display: flex;
    align-items: center;
    margin-top: 6px;
  }
`;

const StoreInfo = styled.div`
  align-self: stretch;
  display: flex;
  align-items: center;
  min-height: 48px;
  margin-top: 16px;
  border-top: solid 1px #d3d7e0;
  border-bottom: solid 1px #d3d7e0;
`;

const Detail = styled.div`
  align-self: stretch;
  > span {
    display: inline-block;
    margin: 12px 0;
  }
  table,
  th,
  td,
  .ant-table-container {
    border-color: #d3d7e0 !important;
  }
  .ant-table-cell {
    padding: 8px 10px;
    background: #fff !important;
    > .ant-input-number {
      max-width: 50px;
    }
  }
  thead .ant-table-cell {
    font-weight: 500;
    white-space: nowrap;
  }
  .price {
    display: flex;
    align-items: center;
    width: 150px;
    > button {
      margin-left: 4px;
    }
    .ant-input-number-group-addon {
      padding: 0 4px;
    }
  }
  .productName {
    position: relative;
    width: 430px;
    > textarea {
      padding-right: 26px;
      resize: none;
    }
    > button {
      position: absolute;
      top: 12px;
      right: 2px;
    }
  }
`;

const Amount = styled.div`
  align-self: flex-end;
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
  > div {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-left: 8px;
    > span {
      display: flex;
      align-items: center;
      height: 38px;
      text-align: right;
      .ant-input-number-group-wrapper {
        width: 128px;
      }
      .ant-input-number-group-addon {
        padding: 0 4px;
      }
    }
    &:first-of-type > div {
      margin-right: -8px;
      border-top: solid 1px #d3d7e0;
    }
  }
  > div > div {
    width: 120%;
    border-top: solid 1px #d3d7e0;
  }
`;

export default InvoiceModal;
